import * as React from "react";
import { ActionType } from "../../../../../../../react-core/Flux/ActionType";
import { ContextActions } from "../../../../../../../react-core/Flux/ContextActions";
import { ContextStore } from "../../../../../../../react-core/Flux/Variables";
import { FormComponent } from "../../../../../../../react-core/Html/FormComponent";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { ContextType } from "../../../../../../../Wrap/Constants/Enums/ContextType";
import { PerformanceMethod } from "../../../../../../../Wrap/Constants/Enums/Domain/PerformanceMethod";
import { ReportContent } from "../../../../../../../Wrap/Constants/Enums/Report/ReportContent";
import { ReportFormat } from "../../../../../../../Wrap/Constants/Enums/Report/ReportFormat";
import { ReportIntroduction } from "../../../../../../../Wrap/Constants/Enums/Report/ReportIntroduction";
import { ReportPerformanceGraph } from "../../../../../../../Wrap/Constants/Enums/Report/ReportPerformanceGraph";
import { ReportView } from "../../../../../../../Wrap/Constants/Enums/Report/ReportView";
import { TaskRequestStatus } from "../../../../../../../Wrap/Constants/Enums/Tasks/TaskRequestStatus";
import { UserClaim } from "../../../../../../../Wrap/Constants/Enums/UserClaim";
import { ClientAccount } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/ClientAccount";
import { ReportBuilder as ReportBuilderModel } from "../../../../../../../Wrap/Models/Models/Reports/ReportBuilder";
import { ReportInfo } from "../../../../../../../Wrap/Models/Models/Reports/ReportInfo";
import AdviserSiteTagManager from "../../../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";
import useApplicationRegistrationCookiePopup from "../../../../../../Shared/Cookies/ChoicesCookie/useApplicationRegistrationCookiePopup.hook";
import { ExcelExportAction } from "../../../../../../Shared/ExcelExport/Actions/ExcelExportAction";
import { ExcelExportStore } from "../../../../../../Shared/ExcelExport/Store/Variable";
import { Permission } from "../../../../../../Shared/Permission/Permission";
import { Navigator } from "../../../../../../Shared/Wizards/Entities/Navigator";
import { Action } from "../../../../../Actions/Action";
import { ClientAccountStore, UserStore } from "../../../../../Stroes/Variable";
import { Urls } from "../../../../../Urls";
import { ConsolidatedAccountStore, PortfolioDashboardMultiSelectionStore, PortfolioDashboardStore } from "../../Store/Variable";
import { isConsolidate, selectedHeadAccounts } from "../Common/PortfolioDashboardHelper";
import { DocumentFileAction } from "../Document/Action/DocumentFileAction";
import { ClientReportAction } from "./Action/ClientReportAction";
import { ReportBuilder } from "./Component/ReportBuilder";
import { ReportReady } from "./Component/ReportReady";
import { ReportContentMap } from "./Resource/Content";

interface IClientReportState {
    pending: boolean;
    generated: boolean;
    clientAccount: ClientAccount;
    reportInfo: ReportInfo;
    url: string;
    isFailed: boolean;
}

interface IClientReportProps {
    customerid: number;
}

export class ClientReport extends FormComponent<IClientReportProps, IClientReportState> {
    constructor(props: IClientReportProps) {
        super(props);
        this.state = {
            pending: false,
            generated: false,
            clientAccount: null,
            reportInfo: null,
            url: null,
            isFailed: false,
        };
        this.subscribeToStore(ClientAccountStore, () => this.onClientAccountStoreChange());
        this.subscribeToStore(ExcelExportStore, () => this.onStoreChange());
    }

    public componentDidMount() {
        Action.getClientAccount(ContextStore.getUrlParamsHierarchyId(ContextType.Account));
        this.addEvent({
            environment: {
                appType: "web",
                setting: "prod",
                timestamps: JsonDateParser.toJsonDateNoOffset(new Date()).toString(),
            },
            page: {
                pageUrl: window.location.pathname,
                pageTitle: `${document.title} - client reporting tool`,
            },
            user: {
                loginId: UserStore.getUserInfo()?.ClientId.toString(),
                internal: "yes",
            },
            event: "pageLoaded",
        });
    }

    private onStoreChange() {
        if (ExcelExportStore.getActionType() == ActionType.Update) {
            const status = ExcelExportStore.getStatus();
            if (
                status == TaskRequestStatus.Pending ||
                status == TaskRequestStatus.Running ||
                status == TaskRequestStatus.Allocated ||
                status == TaskRequestStatus.PendingDelayed ||
                status == TaskRequestStatus.PendingHold
            ) {
                setTimeout(() => {
                    ExcelExportAction.getTaskRequestStatus(this.state.reportInfo.TaskRequestID.toString());
                }, 2000);
            } else if (status == TaskRequestStatus.Complete) {
                DocumentFileAction.get(
                    this.state.clientAccount?.HierarchyId,
                    this.state.reportInfo.FileName,
                    true,
                    0,
                    this.state.reportInfo.TaskRequestID,
                    null,
                    (result: string) => {
                        this.setState({
                            pending: false,
                            url: result,
                        });
                    },
                    true
                );
            } else {
                this.setState({ pending: false, isFailed: true });
            }
        }
    }

    private onClientAccountStoreChange() {
        const clientAccount = ClientAccountStore.getClientAccountInfo();
        this.setState({ clientAccount });
    }

    private addEvent = new AdviserSiteTagManager().addEvent;

    private generateReport(data: ReportBuilderModel) {
        if (isConsolidate()) {
            const consolidateaccounts = [ConsolidatedAccountStore.getData()];
            const isChosenAccountIdInList = consolidateaccounts.some(
                account => account.HeadClAccountId === this.state.clientAccount?.HeadClAccountId
            );
            if (isChosenAccountIdInList){
                const accounts = selectedHeadAccounts(
                    PortfolioDashboardMultiSelectionStore.getData(),
                    PortfolioDashboardStore.getData()
                );
                data.HeadAccountsBesidesConsolidate = accounts.map(account => account.HeadClAccountId);
            }
        }
        data.AccountHierarchyId = this.state.clientAccount?.HierarchyId;
        this.setState({ generated: true, pending: true });
        // Need remove intro options if intro is set to false
        if (data?.ReportContents?.findIndex((x) => x == ReportContent.ReportIntroduction) == -1) {
            // Remove cover page
            data.ReportIntroductions = [];
        }
        this.addEvent({
            event: "clientreportingtool",
            clientReport: {
                dateRange: (data as any).dateSelected == undefined ? "0" : (data as any).dateSelected,
                reportView: data?.ReportView == ReportView.TotalPortfolio ? "total portfolio" : "product view",
                format: data?.ReportFormat == ReportFormat.PDF ? "pdf" : "word",
                accountDetails: data?.ReportContents.some((ele) => ele == ReportContent.AccountDetails) ? "yes" : "no",
                snapshot: data?.ReportContents.some((ele) => ele == ReportContent.Snapshot) ? "yes" : "no",
                valuation: data?.ReportContents.some((ele) => ele == ReportContent.Valuation) ? "yes" : "no",
                performance: data?.ReportContents.some((ele) => ele == ReportContent.Performance) ? "yes" : "no",
                insights: data?.ReportContents.some((ele) => ele == ReportContent.Insights) ? "yes" : "no",
                transactions: data?.ReportContents.some((ele) => ele == ReportContent.Transactions) ? "yes" : "no",
                calculationBasis: data?.PerformanceMethod == PerformanceMethod.IRR ? "irr" : "simple",
            },
            reportingIntroduction: {
                include: data?.ReportContents.some((ele) => ele == ReportContent.ReportIntroduction) ? "yes" : "no",
                cover: data?.ReportIntroductions.some((ele) => ele == ReportIntroduction.CoverPage) ? "yes" : "no",
                contents: data?.ReportIntroductions.some((ele) => ele == ReportIntroduction.ContentsPage)
                    ? "yes"
                    : "no",
                commentary: data?.ReportIntroductions.some((ele) => ele == ReportIntroduction.Commentary)
                    ? "yes"
                    : "no",
                image: data?.ReportIntroductions.some((ele) => ele == ReportIntroduction.Signature) ? "yes" : "no",
            },
            graphs: {
                valuation: data?.ReportPerformanceGraphs.some(
                    (ele) => ele == ReportPerformanceGraph.ValuationMoneyMovements
                )
                    ? "yes"
                    : "no",
                return: data?.ReportPerformanceGraphs.some((ele) => ele == ReportPerformanceGraph.ReturnPerformance)
                    ? "yes"
                    : "no",
                cumulative: data?.ReportPerformanceGraphs.some(
                    (ele) => ele == ReportPerformanceGraph.CumulativePerformance
                )
                    ? "yes"
                    : "no",
                discrete: data?.ReportPerformanceGraphs.some((ele) => ele == ReportPerformanceGraph.DiscretePerformance)
                    ? "yes"
                    : "no",
            },
            breakdown: {
                include: data?.PerformanceIncludeFundBreakdown && data?.ValuationIncludeFundBreakdown ? "yes" : "no",
                valuation: data?.ValuationIncludeFundBreakdown ? "yes" : "no",
                performance: data?.PerformanceIncludeFundBreakdown ? "yes" : "no",
            },
            benchmark: {
                include: data?.BenchmarkList && data?.BenchmarkList.length > 0 ? "yes" : "no",
                noOfBenchmarks:
                    data?.BenchmarkList && data?.BenchmarkList.length > 0 ? data?.BenchmarkList.length.toString() : "0",
            },
        });
        if (this.state?.clientAccount?.InceptionDate >= data?.From) {
            data["fromInception"] = true;
        }
        ClientReportAction.triggerReport(data, (reportInfo: ReportInfo) => {
            this.setState({ reportInfo });
            ExcelExportAction.getTaskRequestStatus(reportInfo.TaskRequestID.toString());
        });
    }

    public render(): JSX.Element {
        const { generated, pending, clientAccount } = this.state;
        if (!clientAccount) {return null;}
        return (
            <div className="ad d1">
                <Permission userClaim={UserClaim.AdviserPortalClientSummaryCanView}>
                    <div className="mb-10">
                        <a
                            onClick={() => {
                                ContextActions.setContext(null, ContextType.Account);
                                Navigator.navigateTo(
                                    Urls.get().main.PortfolioDashboard({
                                        hierarchyId: ContextStore.getUrlParamsHierarchyId(ContextType.Customer),
                                    }).summary
                                );
                            }}
                            className="hyperlink-previous"
                        >
                            <span className="icon" aria-hidden={true}></span>
                            <span className="text">Back to summary</span>
                        </a>
                    </div>
                </Permission>
                <h2>Client portfolio report</h2>
                <hr />
                {generated ? (
                    <ReportReady pending={pending} url={this.state.url} isFailed={this.state.isFailed} />
                ) : (
                    <ReportBuilder
                        onGenerate={(data) => this.generateReport(data)}
                        data={clientAccount}
                        onChange={() => {}}
                        onRef={() => {}}
                    />
                )}
            </div>
        );
    }
}
